import * as Units from '../../Units';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint} from '../types';

export interface WindRunChartOptions {
  /** Data with WIND_RUN_CUMULATIVE and WIND_RUN_DAILY keys */
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  height?: number;
  /** Label for cumulative series */
  cumulativeLabel?: string;
  /** Label for daily series */
  dailyLabel?: string;
}

export const windRunChart = (options: WindRunChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    height = 400,
    cumulativeLabel = 'Wind Run',
    dailyLabel = 'Day',
  } = options;

  const distanceUnit = Units.getUnitLabel(units, 'Distance');

  return {
    height,
    data,
    series: [
      {
        id: 'wind-run-cumulative',
        type: 'area',
        dataKey: 'WIND_RUN_CUMULATIVE',
        name: cumulativeLabel,
        color: theme.colors.primary,
        yAxisIndex: 0,
        interpolation: 'monotone',
        fillOpacity: 0.3,
        lineWidth: 2,
        showMarkers: false,
        zIndex: 0,
      },
      {
        id: 'wind-run-daily',
        type: 'bar',
        dataKey: 'WIND_RUN_DAILY',
        name: dailyLabel,
        color: theme.colors.info,
        yAxisIndex: 1,
        interpolation: 'linear',
        opacity: 0.5,
        showMarkers: false,
        zIndex: 1,
      },
    ],
    xAxis: createXAxisConfig({
      type: 'datetime',
      timezone,
      granularity: 'D',
      theme,
    }),
    yAxes: [
      {
        ...createYAxisConfig({
          id: 'cumulative',
          theme,
          unitLabel: distanceUnit,
          domain: [0, 'auto'],
          precision: 0,
        }),
        label: cumulativeLabel,
      },
      {
        ...createYAxisConfig({
          id: 'daily',
          theme,
          unitLabel: distanceUnit,
          position: 'right',
          domain: [0, 'auto'],
          precision: 0,
          gridLines: false,
        }),
        label: dailyLabel,
      },
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity: 'D',
      metric: 'FF',
      units,
      theme,
      customFormatters: {
        'wind-run-cumulative': (value: number) =>
          `${Math.round(value)} ${distanceUnit}`,
        'wind-run-daily': (value: number) =>
          `${Math.round(value)} ${distanceUnit}`,
      },
    }),
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
